import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Search, X, Loader, AlertCircle, Clock, Calendar, BookOpen, Users, MapPin } from 'lucide-react';
import { searchAllContent } from '../lib/seo';
import VoiceSearch from './VoiceSearch';

interface SearchResult {
  objectID: string;
  title: string;
  description?: string;
  excerpt?: string;
  slug?: string;
  author?: string;
  category?: string;
  read_time?: number;
  published_at?: string;
  duration?: string;
  level?: string;
  price?: number;
  type?: 'blog' | 'course' | 'myth' | 'nonprofit';
  url?: string;
  explanation?: string;
  location?: string;
}

interface SearchResultsProps {
  results: {
    blogPosts: SearchResult[];
    courses: SearchResult[];
    myths: SearchResult[];
    nonprofits: SearchResult[];
    authors: SearchResult[];
    categories: SearchResult[];
    surveys: SearchResult[];
  };
  onClose: () => void;
}

const SearchResults: React.FC<SearchResultsProps> = ({ results, onClose }) => {
  const location = useLocation();
  const isCourseSection = location.pathname.startsWith('/course');
  const hasResults = Object.values(results).some(arr => arr.length > 0);

  if (!hasResults) {
    return (
      <div className="p-4 text-center text-gray-600">
        No results found
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-200">
      {/* Show courses first if we're in the courses section */}
      {isCourseSection && results.courses.length > 0 && (
        <div className="p-4">
          <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
            <BookOpen className="h-4 w-4 mr-2" />
            Courses
          </h3>
          <div className="space-y-3">
            {results.courses.map(course => (
              <Link
                key={course.objectID}
                to={`/course/${course.slug}`}
                onClick={onClose}
                className="block hover:bg-gray-50 -mx-4 px-4 py-2 transition-colors duration-200"
              >
                <h4 className="text-base font-medium text-gray-900">{course.title}</h4>
                {course.description && (
                  <p className="text-sm text-gray-600 line-clamp-2 mt-1">{course.description}</p>
                )}
                <div className="flex items-center text-xs text-gray-500 mt-2">
                  {course.level && (
                    <span className="flex items-center">
                      <Users className="h-3 w-3 mr-1" />
                      {course.level}
                    </span>
                  )}
                  {course.duration && (
                    <>
                      <span className="mx-2">•</span>
                      <span className="flex items-center">
                        <Clock className="h-3 w-3 mr-1" />
                        {course.duration}
                      </span>
                    </>
                  )}
                  {course.location && (
                    <>
                      <span className="mx-2">•</span>
                      <span className="flex items-center">
                        <MapPin className="h-3 w-3 mr-1" />
                        {course.location}
                      </span>
                    </>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Show Non-profit Resources */}
      {results.nonprofits && results.nonprofits.length > 0 && (
        <div className="p-4">
          <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
            <BookOpen className="h-4 w-4 mr-2" />
            Non-Profit Resources
          </h3>
          <div className="space-y-3">
            {results.nonprofits.map(resource => (
              <Link
                key={resource.objectID}
                to={resource.url || '/'}
                onClick={onClose}
                className="block hover:bg-gray-50 -mx-4 px-4 py-2 transition-colors duration-200"
              >
                <h4 className="text-base font-medium text-gray-900">{resource.title}</h4>
                {resource.description && (
                  <p className="text-sm text-gray-600 line-clamp-2 mt-1">{resource.description}</p>
                )}
                {resource.location && (
                  <div className="flex items-center text-xs text-gray-500 mt-2">
                    <MapPin className="h-3 w-3 mr-1" />
                    <span>{resource.location}</span>
                  </div>
                )}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Show AI Myths */}
      {results.myths && results.myths.length > 0 && (
        <div className="p-4">
          <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
            <BookOpen className="h-4 w-4 mr-2" />
            AI Myths & Facts
          </h3>
          <div className="space-y-3">
            {results.myths.map(myth => (
              <Link
                key={myth.objectID}
                to="/#ai-myths"
                onClick={onClose}
                className="block hover:bg-gray-50 -mx-4 px-4 py-2 transition-colors duration-200"
              >
                <h4 className="text-base font-medium text-gray-900">{myth.title}</h4>
                {myth.explanation && (
                  <p className="text-sm text-gray-600 line-clamp-2 mt-1">{myth.explanation}</p>
                )}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Show blog posts */}
      {results.blogPosts.length > 0 && (
        <div className="p-4">
          <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
            <BookOpen className="h-4 w-4 mr-2" />
            Blog Posts
          </h3>
          <div className="space-y-3">
            {results.blogPosts.map(post => (
              <Link
                key={post.objectID}
                to={`/blog/${post.slug}`}
                onClick={onClose}
                className="block hover:bg-gray-50 -mx-4 px-4 py-2 transition-colors duration-200"
              >
                <h4 className="text-base font-medium text-gray-900">{post.title}</h4>
                {post.excerpt && (
                  <p className="text-sm text-gray-600 line-clamp-2 mt-1">{post.excerpt}</p>
                )}
                <div className="flex items-center text-xs text-gray-500 mt-2">
                  {post.published_at && (
                    <>
                      <Calendar className="h-3 w-3 mr-1" />
                      <span>{new Date(post.published_at).toLocaleDateString()}</span>
                      <span className="mx-2">•</span>
                    </>
                  )}
                  {post.read_time && (
                    <>
                      <Clock className="h-3 w-3 mr-1" />
                      <span>{post.read_time} min read</span>
                    </>
                  )}
                  {post.location && (
                    <>
                      <span className="mx-2">•</span>
                      <MapPin className="h-3 w-3 mr-1" />
                      <span>{post.location}</span>
                    </>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Show courses last if we're not in the courses section */}
      {!isCourseSection && results.courses.length > 0 && (
        <div className="p-4">
          <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
            <BookOpen className="h-4 w-4 mr-2" />
            Courses
          </h3>
          <div className="space-y-3">
            {results.courses.map(course => (
              <Link
                key={course.objectID}
                to={`/course/${course.slug}`}
                onClick={onClose}
                className="block hover:bg-gray-50 -mx-4 px-4 py-2 transition-colors duration-200"
              >
                <h4 className="text-base font-medium text-gray-900">{course.title}</h4>
                {course.description && (
                  <p className="text-sm text-gray-600 line-clamp-2 mt-1">{course.description}</p>
                )}
                <div className="flex items-center text-xs text-gray-500 mt-2">
                  {course.level && (
                    <span className="flex items-center">
                      <Users className="h-3 w-3 mr-1" />
                      {course.level}
                    </span>
                  )}
                  {course.duration && (
                    <>
                      <span className="mx-2">•</span>
                      <span className="flex items-center">
                        <Clock className="h-3 w-3 mr-1" />
                        {course.duration}
                      </span>
                    </>
                  )}
                  {course.location && (
                    <>
                      <span className="mx-2">•</span>
                      <span className="flex items-center">
                        <MapPin className="h-3 w-3 mr-1" />
                        {course.location}
                      </span>
                    </>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default function SearchBox() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<any>({
    blogPosts: [],
    courses: [],
    myths: [],
    nonprofits: [],
    authors: [],
    categories: [],
    surveys: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const searchTimeout = setTimeout(async () => {
      if (query.trim()) {
        setLoading(true);
        setError(null);
        try {
          const searchResults = await searchAllContent(query);
          setResults(searchResults);
          setIsOpen(true);
        } catch (error) {
          console.error('Search error:', error);
          setError('Search is currently unavailable. Please try again later.');
        } finally {
          setLoading(false);
        }
      } else {
        setResults({
          blogPosts: [],
          courses: [],
          myths: [],
          nonprofits: [],
          authors: [],
          categories: [],
          surveys: []
        });
        setIsOpen(false);
        setError(null);
      }
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [query]);

  const handleClose = () => {
    setQuery('');
    setIsOpen(false);
    setResults({
      blogPosts: [],
      courses: [],
      myths: [],
      nonprofits: [],
      authors: [],
      categories: [],
      surveys: []
    });
  };

  const handleVoiceSearch = (transcript: string) => {
    setQuery(transcript);
  };

  return (
    <div className="relative" ref={searchRef}>
      <div className="space-y-4">
        <div className="relative">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search courses, content, and AI myths..."
            className="w-full px-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F98B3D] focus:border-transparent"
            aria-label="Search"
          />
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          {query && (
            <button
              onClick={handleClose}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label="Clear search"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        <VoiceSearch onSearch={handleVoiceSearch} />
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 max-h-[80vh] overflow-y-auto z-50">
          {loading ? (
            <div className="flex items-center justify-center p-4">
              <Loader className="h-6 w-6 text-[#F98B3D] animate-spin" />
            </div>
          ) : error ? (
            <div className="flex items-center p-4 text-red-600">
              <AlertCircle className="h-5 w-5 mr-2" />
              <span className="text-sm">{error}</span>
            </div>
          ) : (
            <SearchResults results={results} onClose={handleClose} />
          )}
        </div>
      )}
    </div>
  );
}